import React from 'react';
import { Calendar, MapPin, Phone, Sparkles, HeartHandshake, ShieldCheck, Landmark } from 'lucide-react';
import ProtectedContact, { ENCODED_CONTACTS } from './ProtectedContact';

export default function HomeSection({ setActiveTab }) {
  return (
    <div className="space-y-12 pb-12">
      
      {/* Hero Banner Section */}
      <section className="relative rounded-2xl overflow-hidden shadow-lg border border-amber-600/30">
        <div className="hero-banner-light p-8 md:p-14 flex flex-col justify-center items-center text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/20 backdrop-blur-md border border-amber-300 text-amber-100 text-xs font-semibold uppercase tracking-wider mb-6 shadow-sm">
            <Sparkles className="w-4 h-4 text-amber-300" />
            美國媽祖基金會 ‧ 官方主頁
          </div>
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-black text-white drop-shadow-md leading-tight mb-4 font-serif">
            美國媽祖基金會
          </h1>
          <p className="text-lg sm:text-2xl text-amber-200 font-serif font-medium mb-8 max-w-3xl">
            American Mazu Foundation Inc.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button 
              onClick={() => setActiveTab('about')}
              className="btn-gold-light shadow-md text-sm sm:text-base"
            >
              了解我們 About Us
            </button>
            <button 
              onClick={() => setActiveTab('culture')}
              className="btn-outline-red text-sm sm:text-base border-white text-white hover:bg-white hover:text-[#8B0000]"
            >
              媽祖文化 Culture & History
            </button>
          </div>
        </div>
      </section>

      {/* Featured Historic Event Announcement */}
      <section className="max-w-5xl mx-auto">
        <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-10 shadow-sm space-y-6">
          
          <div className="flex flex-col md:flex-row gap-8 items-center border-b border-gray-200 pb-8">
            <div className="w-full md:w-1/3 flex justify-center">
              <div className="relative">
                <img 
                  src="/assets/MAZU.jpg" 
                  alt="Mazu Statue" 
                  className="w-48 h-48 md:w-56 md:h-56 object-cover rounded-2xl border-4 border-amber-600 shadow-md"
                />
                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-[#8B0000] text-amber-100 text-xs px-3 py-1 rounded-full font-bold shadow-md whitespace-nowrap">
                  天上聖母 ‧ 湄洲分靈
                </div>
              </div>
            </div>

            <div className="w-full md:w-2/3 space-y-4 text-gray-900">
              <div className="inline-block bg-[#8B0000] text-white text-xs font-bold px-3 py-1 rounded-md uppercase tracking-wider">
                ★ 歷史重磅盛事 Historic Announcement
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#8B0000] font-serif leading-snug">
                湄洲媽祖祖廟神靈來了！
                <span className="block text-lg font-sans text-amber-800 font-medium mt-1">
                  Goddess Mazu is coming to New York !
                </span>
              </h2>
              <div className="text-gray-500 text-xs flex items-center gap-2 border-b border-gray-200 pb-3">
                <Calendar className="w-4 h-4 text-[#8B0000]" />
                <span>發布日期：2025年4月15日 | 發布者：mindyh</span>
              </div>
              <p className="text-gray-700 text-base sm:text-lg leading-relaxed font-serif">
                熱烈歡迎媽祖祖廟董事長林金榜親率法師團一行專程赴美護駕媽祖分靈聖像安宮紐約，并為美國媽祖聖像開光剪綵，祈福為紐約信眾降福納祥，消災避難，閤家平安！
              </p>
            </div>
          </div>

          {/* Event Details Grid */}
          <div className="bg-amber-50/70 border border-amber-200 rounded-xl p-6 grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-800">
            <div className="space-y-2">
              <h3 className="text-lg font-bold text-[#8B0000] flex items-center gap-2 border-b border-amber-300 pb-2 font-serif">
                <Landmark className="w-5 h-5 text-[#8B0000]" />
                隆重開光大典 (Consecration Ceremony)
              </h3>
              <p className="text-sm">
                <strong className="text-[#8B0000]">時間 Date:</strong> 2025年5月11日（星期一）媽祖誕辰1055周年慶典日 上午9時
              </p>
              <p className="text-sm">
                <strong className="text-[#8B0000]">地點 Location:</strong> <ProtectedContact encoded={ENCODED_CONTACTS.CEREMONY_LOCATION} />
              </p>
            </div>

            <div className="space-y-2">
              <h3 className="text-lg font-bold text-[#8B0000] flex items-center gap-2 border-b border-amber-300 pb-2 font-serif">
                <Sparkles className="w-5 h-5 text-[#8B0000]" />
                盛大慶典宴會 (Gala Celebration Banquet)
              </h3>
              <p className="text-sm">
                <strong className="text-[#8B0000]">時間 Date:</strong> 當日（5月11日）晚上6時半憑票入場
              </p>
              <p className="text-sm">
                <strong className="text-[#8B0000]">地點 Location:</strong> 君豪大酒樓 (Royal Queen Restaurant, Flushing NY)
              </p>
            </div>

            <div className="md:col-span-2 pt-2 border-t border-amber-200 flex flex-col sm:flex-row justify-between items-center gap-4">
              <p className="text-sm text-gray-700 font-medium">
                誠邀社會賢達、政商名流，善男信女，十方大德，歡聚一堂，共襄盛舉！
              </p>
              <ProtectedContact 
                encoded={ENCODED_CONTACTS.PHONE} 
                type="tel"
                prefix="聯繫電話："
                className="flex items-center gap-2 bg-[#8B0000] hover:bg-[#600000] text-white text-sm px-5 py-2.5 rounded-lg font-bold shadow transition-colors whitespace-nowrap"
              >
                <Phone className="w-4 h-4 text-amber-300" />
              </ProtectedContact>
            </div>
          </div>

        </div>
      </section>

      {/* Foundation Core Pillars */}
      <section className="max-w-6xl mx-auto pt-4">
        <div className="text-center space-y-2 mb-8">
          <h2 className="text-2xl sm:text-4xl font-bold text-[#8B0000] font-serif">
            基金會三大宗旨與核心價值
          </h2>
          <p className="text-gray-600 text-sm sm:text-base">
            傳承千年媽祖信俗，弘揚立德、行善、博愛的精神美德
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white border border-gray-200 rounded-xl p-6 flex flex-col items-center text-center shadow-sm hover:shadow-md transition-shadow">
            <div className="w-14 h-14 rounded-full bg-red-50 border border-red-200 flex items-center justify-center text-[#8B0000] mb-4">
              <HeartHandshake className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-[#8B0000] mb-2 font-serif">立德 ‧ 行善</h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              傳承媽祖慈悲濟世的精神，倡導社會公益與同胞互助，關懷弱勢群體。
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-6 flex flex-col items-center text-center shadow-sm hover:shadow-md transition-shadow">
            <div className="w-14 h-14 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center text-[#B8860B] mb-4">
              <ShieldCheck className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-[#8B0000] mb-2 font-serif">博愛 ‧ 和平</h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              聯合國教科文組織認定的非物質文化遺產，促進中美民間文化交流與和平。
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-6 flex flex-col items-center text-center shadow-sm hover:shadow-md transition-shadow">
            <div className="w-14 h-14 rounded-full bg-red-50 border border-red-200 flex items-center justify-center text-[#8B0000] mb-4">
              <Landmark className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-[#8B0000] mb-2 font-serif">文化傳承</h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              在大洋彼岸建立信仰紐帶，凝聚海外華僑與同胞的愛國愛鄉情感。
            </p>
          </div>
        </div>
      </section>

    </div>
  );
}
